import type { Theme } from '@mui/material/styles';
import { mergeSx } from '../../utils/styles';

const getSharedFieldRootStyles = (theme: Theme) => ({
  minHeight: 44,
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'neutral.500',
  backgroundColor: 'neutral.600',
  transition: theme.transitions.create(['border-color', 'background-color'], {
    duration: theme.transitions.duration.shorter,
  }),
  '& .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-notchedOutline': {
    display: 'none',
  },
  '&:hover': {
    borderColor: 'neutral.500',
    backgroundColor: 'neutral.600',
  },
  '&.Mui-focused': {
    borderColor: 'secondary.main',
  },
  '&.Mui-error': {
    borderColor: 'error.light',
  },
  '&.Mui-disabled': {
    opacity: 0.52,
  },
});

const getSharedLabelStyles = {
  color: 'neutral.400',
  fontSize: 15,
  fontWeight: 700,
  letterSpacing: 0,
  transform: 'translate(16px, 15px) scale(1)',
  transformOrigin: 'top left',
  pointerEvents: 'none',
  '&.MuiInputLabel-shrink': {
    color: 'neutral.300',
    transform: 'translate(20px, 8px) scale(0.6)',
  },
  '&.Mui-focused': {
    color: 'neutral.400',
  },
  '&.Mui-error': {
    color: 'error.light',
  },
  '&.Mui-disabled': {
    color: 'neutral.200',
  },
};

const getSharedHelperTextStyles = {
  mt: 1,
  ml: 1.5,
  color: 'neutral.400',
  fontSize: 12,
  fontWeight: 600,
  lineHeight: '16px',
  '&.Mui-error': {
    color: 'error.light',
  },
};

export const neobetFormFieldStyles = {
  textField: (theme: Theme) => ({
    width: '100%',
    '& .MuiOutlinedInput-root, & .MuiPickersOutlinedInput-root': {
      ...getSharedFieldRootStyles(theme),
      '& .MuiInputAdornment-root .MuiButtonBase-root': {
        mr: -0.5,
        color: 'neutral.400',
        '&:hover': {
          backgroundColor: 'transparent',
          color: 'neutral.500',
        },
      },
      '& .MuiInputAdornment-root .MuiSvgIcon-root': {
        fontSize: 21,
      },
    },
    '& .MuiPickersOutlinedInput-root': {
      px: 0,
      '&.Mui-focused': {
        borderColor: 'neutral.500',
      },
      '& .MuiInputAdornment-root .MuiButtonBase-root': {
        mr: 1.5,
      },
    },
    '& .MuiInputLabel-root': {
      ...getSharedLabelStyles,
    },
    '& .MuiInputBase-input, & .MuiPickersInputBase-sectionsContainer': {
      p: '20px 20px 9px',
      color: 'neutral.50',
      fontSize: 15,
      fontWeight: 700,
      lineHeight: '20px',
      letterSpacing: 0,
      '&::placeholder': {
        color: 'neutral.400',
        opacity: 1,
      },
    },
    '& .MuiInputBase-input:-webkit-autofill, & .MuiInputBase-input:-webkit-autofill:hover, & .MuiInputBase-input:-webkit-autofill:focus, & .MuiInputBase-input:-webkit-autofill:active':
      {
        WebkitTextFillColor: `${theme.palette.neutral?.[50] ?? theme.palette.text.primary} !important`,
        caretColor: `${theme.palette.neutral?.[50] ?? theme.palette.text.primary} !important`,
      },
    '& .MuiFormHelperText-root': {
      ...getSharedHelperTextStyles,
    },
  }),
  selectControl: (theme: Theme) => ({
    width: '100%',
    '& .MuiInputLabel-root': {
      ...getSharedLabelStyles,
    },
    '& .MuiOutlinedInput-root': {
      ...getSharedFieldRootStyles(theme),
      '& .MuiSelect-icon': {
        right: 18,
        color: 'neutral.400',
        fontSize: 18,
      },
    },
    '& .MuiFormHelperText-root': {
      ...getSharedHelperTextStyles,
    },
  }),
  select: {
    '&& .MuiSelect-select': {
      p: '20px 46px 9px 20px !important',
      color: 'neutral.50',
      fontSize: 15,
      fontWeight: 700,
      lineHeight: '20px',
      letterSpacing: 0,
      textAlign: 'left',
      textAlignLast: 'left',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
    },
  },
  menuPaper: {
    mt: 1,
    borderRadius: 2.5,
    border: '1px solid',
    borderColor: 'secondary.500',
    '& .MuiList-root': {
      paddingTop: 0,
      paddingBottom: 0,
    },
  },
  checkboxLabel: {
    alignItems: 'center',
    m: 0,
    minHeight: 24,
    gap: 1,
    color: 'neutral.200',
    '& .MuiFormControlLabel-label': {
      fontSize: 14,
      fontWeight: 700,
      lineHeight: '20px',
      display: 'inline-flex',
      alignItems: 'center',
    },
  },
  checkbox: {
    p: 0,
    color: 'neutral.400',
    '& .MuiSvgIcon-root': {
      fontSize: 22,
    },
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&.Mui-checked': {
      color: 'primary.main',
    },
  },
  datePickerPaper: {
    width: 320,
    maxWidth: 'calc(100vw - 40px)',
    mt: 0.5,
    overflow: 'hidden',
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'secondary.main',
    backgroundColor: 'neutral.600',
    backgroundImage: 'none',
    boxShadow: 6,
  },
  datePickerLayout: {
    width: '100%',
    backgroundColor: 'transparent',
    '& .MuiDateCalendar-root': {
      width: '100%',
      maxWidth: 320,
    },
    '& .MuiPickersCalendarHeader-label': {
      color: 'neutral.25',
      typography: 'body2',
      fontWeight: 700,
    },
    '& .MuiPickersCalendarHeader-switchViewButton, & .MuiPickersArrowSwitcher-button': {
      color: 'neutral.200',
      '&:hover': {
        backgroundColor: 'neutral.A100',
      },
    },
    '& .MuiPickersCalendarHeader-switchViewButton .MuiSvgIcon-root, & .MuiPickersArrowSwitcher-button .MuiSvgIcon-root':
      {
        color: 'inherit',
      },
    '& .MuiDayCalendar-weekDayLabel': {
      color: 'neutral.50',
      typography: 'body3',
      fontWeight: 800,
    },
    '& .MuiPickerDay-root': {
      color: 'neutral.100',
      typography: 'body3',
      '&:hover, &:focus, &.Mui-focusVisible': {
        backgroundColor: 'neutral.A100',
      },
      '&.MuiPickerDay-today': {
        borderColor: 'neutral.300',
      },
      '&.Mui-selected': {
        color: 'neutral.900',
        backgroundColor: 'primary.main',
        fontWeight: 700,
        '&:hover, &:focus, &.Mui-focusVisible': {
          backgroundColor: 'primary.main',
        },
      },
      '&.Mui-disabled': {
        color: 'neutral.400',
      },
    },
    '& .MuiYearCalendar-button': {
      color: 'neutral.100',
      typography: 'body3',
      fontWeight: 700,
      borderRadius: 2,
      '&:hover, &:focus, &.Mui-focusVisible': {
        backgroundColor: 'neutral.A100',
      },
      '&.Mui-selected': {
        color: 'neutral.900',
        backgroundColor: 'primary.main',
        '&:hover, &:focus, &.Mui-focusVisible': {
          backgroundColor: 'primary.main',
        },
      },
      '&.Mui-disabled': {
        color: 'neutral.400',
      },
    },
  },
};

export const neobetFormFieldSx = {
  text: neobetFormFieldStyles.textField,
  select: mergeSx(neobetFormFieldStyles.selectControl, neobetFormFieldStyles.select),
};
